import * as fileApi from '@/api/file';
import { jobStateEnum } from '@/api/job';
import { Job, jobService } from '@/utils/jobService';
import pathbe from 'path-browserify-esm';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
interface OutputProps {
    job?: Job;
}

// Any state after which the output is final. A failed calculation's log is exactly what the
// user needs to read, so fetching only on COMPLETE hid the most useful case.
const terminalStates = new Set<number>([
    jobStateEnum.COMPLETE,
    jobStateEnum.CANCELED,
    jobStateEnum.FAILED,
    jobStateEnum.TIMEOUT,
    jobStateEnum.NODE_FAIL,
    jobStateEnum.PREEMPTED,
    jobStateEnum.BOOT_FAIL,
    jobStateEnum.DEADLINE,
    jobStateEnum.OOM,
]);

const isTerminal = (state?: jobStateEnum) => state !== undefined && terminalStates.has(state);

// outputPathFor resolves where this job's stdout can be read.
//
// A remote job has no <name>.<scheduler-id>.out: the helper's script sends both stdout and
// stderr to .aitomistic/stdout.log on the remote side, and the pull saves it in the working
// directory as <cluster>-<scheduler id>.out. Deriving the local scheduler's naming for a
// remote job therefore points at a file that never existed.
const outputPathFor = (job: Partial<Job>): string | undefined => {
    if (!job.path) return undefined;
    const remoteID = job.id_job || job.job_uid;
    if (job.cluster && remoteID) {
        return pathbe.join(pathbe.dirname(job.path), `${job.cluster}-${remoteID}.out`);
    }
    let outputfileName = `${job.job_name}.${job.id_job}.out`;
    if (job.path.endsWith('.com')) {
        const baseName = pathbe.basename(job.path, '.com');
        outputfileName = `${baseName}.log`;
    }
    return pathbe.join(pathbe.dirname(job.path), outputfileName);
};

// readOutput treats a missing file as "no output" rather than an error, so a fallback path can
// still be tried and the panel can say there is nothing to show.
const readOutput = async (path?: string): Promise<string | undefined> => {
    if (!path) return undefined;
    try {
        return await fileApi.getRawFileResource(path);
    } catch {
        return undefined;
    }
};

// const JobStateMap = {
//     0: 'PENDING',
//     1: 'RUNNING',
//     2: 'COMPLETE',
//     4: 'CANCELED',
// }

const OutputDisplay = (({ job }: OutputProps) => {
    const outputRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('common');

    const getContent = async (job: Partial<Job>) => {
        try {
            if (outputRef.current && job.path) {
                const outputfilePath = outputPathFor(job);
                let content = await readOutput(outputfilePath);
                if (!content && job.cluster && job.job_uid) {
                    // Jobs retrieved by an earlier build kept the log under
                    // .aitomistic/<job_uid>.out. Read it rather than losing output that is
                    // already on disk.
                    content = await readOutput(
                        pathbe.join(pathbe.dirname(job.path), '.aitomistic', `${job.job_uid}.out`),
                    );
                }
                if (content) {

                    outputRef.current.innerHTML = content.split('\n').map(line => `<div class="leading-relaxed">${line}</div>`).join('');
                    scrollToBottom();
                } else {
                    outputRef.current.innerHTML = `<div class="leading-relaxed">${t('output_display.no_output')}</div>`;
                    scrollToBottom();
                }
            }
        } catch (e) {
            console.error(e);
            if (outputRef.current) {
                outputRef.current.innerHTML = `<div class="leading-relaxed">${t('output_display.no_output')}</div>`;
            }
        }
    }

    const updateCallBack = (tag: string, job: Partial<Job>) => {
        if (outputRef.current && !!job && job?.tag === tag) {
            if (isTerminal(job.state)) {
                getContent(job);
            } else if (job.state === jobStateEnum.RUNNING || job.state === jobStateEnum.PENDING) {
                scrollToBottom();
            }
        }
    }

    const listenJob = () => {
        jobService.setUpdateJobCallback(job?.tag || "", updateCallBack);
    }


    useEffect(() => {
        // Clear any existing callback first
        jobService.removeUpdateJobCallback(job?.tag!);

        if ((job?.state === jobStateEnum.RUNNING || job?.state === jobStateEnum.PENDING) && job?.tag) {
            listenJob();
            if (outputRef.current) {
                outputRef.current.innerHTML = `<div class="leading-relaxed">${t('output_display.submitted')}</div>`;
            }
            console.log("Registered job callback for path:", job.tag);
        } else if (isTerminal(job?.state) && job?.tag) {
            getContent(job);
            console.log("Job is complete, fetching content for path:", job.tag);
        }

        return () => {
            jobService.removeUpdateJobCallback(job?.tag!);
        };
    }, [job?.state, job?.id_job, job?.tag]);


    const scrollToBottom = () => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    };

    return (
        <div className="terminal-container page-container">
            <div
                ref={outputRef}
                className="bg-black dark:bg-[#1E1E1E] text-[#ECECEC] font-mono p-3 h-full overflow-y-auto"
                style={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                }}
            />
        </div>
    );
});

export default OutputDisplay;
